import React, { useEffect, useMemo, useRef, useState } from "react";
import invoke from "../../common/utils/invoke";
import FormSelect from "../../common/components/FormSelect";
import FormInput from "../../common/components/FormInput";
import Modal from "../../common/components/Modal.jsx";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../../constants";
import {
  validateBranchContactForm,
  validateBranchContactField,
  stripHTMLTags,
} from "../../common/utils/validation";
import "./BranchLocatorForm.scss";
import IndiaFlag from "../../assets/images/india-flag.png";
import BrandLoader from "../../common/components/BrandLoader.jsx";
 
const BranchLocatorForm = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [locatecityError, setLocatecityError] = useState("");
  const [locatestateError, setLocatestateError] = useState("");
  const [isFetchingBranches  , setIsFetchingBranches ] = useState(false);
  const [selection, setSelection] = useState({ state: "", city: "" });
  const [locatedSelection, setLocatedSelection] = useState({
    state: "",
    city: "",
  });
  const [hasLocated, setHasLocated] = useState(false);
 
  const [activeBranchId, setActiveBranchId] = useState("");
  const [contactForm, setContactForm] = useState({
    mobileNumber: "",
    email: "",
  });
  const [contactErrors, setContactErrors] = useState({});
  const locateTimeoutRef = useRef(null);
 
  /* ---------------- Fetch Branches ---------------- */
  useEffect(() => {
    const fetchBranches = async () => {
      setIsFetchingBranches(true);
      try {
        const res = await invoke.fetchBranchLocator();
        setBranches(Array.isArray(res?.items) ? res.items : []);
      } catch (err) {
        // Failed to load branch data
      } finally {
        setIsFetchingBranches(false);
      }
    };
    fetchBranches();
    return () => {
      if (locateTimeoutRef.current) {
        clearTimeout(locateTimeoutRef.current);
      }
    };
  }, []);

 
  /* ---------------- Dropdown Data ---------------- */
  const states = useMemo(
    () =>
      [...new Set(branches.map((b) => b.branchState).filter(Boolean))].sort(),
    [branches]
  );
 
  const cities = useMemo(() => {
    if (!selection.state) return [];
    return [
      ...new Set(
        branches
          .filter((b) => b.branchState === selection.state)
          .map((b) => b.branchCity)
          .filter(Boolean)
      ),
    ].sort();
  }, [branches, selection.state]);
 
  const branchesInSelection = useMemo(() => {
    if (
      !hasLocated ||
      !locatedSelection.state ||
      !locatedSelection.city
    )
      return [];
    return branches.filter(
      (b) =>
        b.branchState === locatedSelection.state &&
        b.branchCity === locatedSelection.city
    );
  }, [branches, locatedSelection, hasLocated]);
 
  const selectedBranch = useMemo(() => {
    if (!activeBranchId) return null;
    return (
      branchesInSelection.find(
        (b) => String(b.id) === String(activeBranchId)
      ) || null
    );
  }, [branchesInSelection, activeBranchId]);
 
  /* ---------------- Handlers ---------------- */
  const handleStateChange = (e) => {
    setSelection({ state: e.target.value, city: "" });
    setLocatestateError("");
  };
 
  const handleCityChange = (e) => {
    setSelection((prev) => ({ ...prev, city: e.target.value }));
    setLocatecityError("");
  };
 
  const handleLocate = () => {
    if (!selection.state) {
      setLocatestateError("Please select a State");
      return;
    }
 
    if (!selection.city) {
      setLocatecityError("Please select a City");
      return;
    }
    setLoading(true);
    setLocatecityError("");
    setLocatestateError("");
    if (locateTimeoutRef.current) {
      clearTimeout(locateTimeoutRef.current);
    }
    locateTimeoutRef.current = setTimeout(() => {
      // Persist the selection used for this locate action
      setLocatedSelection(selection);
      setHasLocated(true);
      setLoading(false);
      locateTimeoutRef.current = null;
    }, 1000);
  };
 
  const openContactForBranch = (id) => {
    setActiveBranchId(String(id));
    setContactErrors({});
  };
 
  const handleContactChange = (e) => {
    const { name, value } = e.target;
 
    setContactForm((prev) => ({ ...prev, [name]: value }));
 
    const fieldError = validateBranchContactField(name, value, {
      ...contactForm,
      [name]: value,
    });
 
    setContactErrors((prev) => ({ ...prev, [name]: fieldError }));
  };
 
  const handleSend = async (e) => {
    e.preventDefault();
 
    const errors = validateBranchContactForm(contactForm);
    if (Object.keys(errors).length) {
      setContactErrors(errors);
      return;
    }
 
    if (!selectedBranch) {
      // Shouldn't happen if UI flow is followed, but keep it safe.
      setContactErrors((prev) => ({
        ...prev,
        branch: "Please select a branch",
      }));
      return;
    }
 
    setLoading(true);
    setApiErrorMsg("");
    try {
      const res = await invoke.sendBranchDetails({
        ...contactForm,
        branch: selectedBranch,
      });
      if (res) {
        setContactForm({ mobileNumber: "", email: "" });
        setActiveBranchId("");
        setShowSuccessModal(true);
      } else {
        throw new Error("Submission failed");
      }
    } catch (err) {
      setApiErrorMsg(
        err?.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      );
      setShowErrorModal(true);
      setContactForm({ mobileNumber: "", email: "" });
      setActiveBranchId("");
    } finally {
      setLoading(false);
    }
  };
 
 
  return (
    <div className="branch-form-wrapper">
      {loading && (
        <div className="loader-overlay">
          <BrandLoader color="#97262A" loading={loading} size={15} />
        </div>
      )}
      <div className="branch-locater-banner">
        <div className="left-side-wraper">
          <h1 className="bold h3">Find A Branch Near You</h1>
          <p className="regular-body">
            Our nearby branches are designed to make in-person support easily
            accessible whenever you need it.
          </p>
        </div>
 
        <div className="right-side-wraper">
          <h5 className="form-title semibold">In-person support, closer to you</h5>
 
          <div className="branch-form">
            <FormSelect
              label="State"
              value={selection.state}
              onChange={handleStateChange}
              options={isFetchingBranches ? [] : states}
              placeholder={isFetchingBranches ? "Loading State..." : "Select State"}
              error={locatestateError}
              disabled={isFetchingBranches}
            />
 
            <FormSelect
              label="City"
              value={selection.city}
              onChange={handleCityChange}
              options={isFetchingBranches ? [] : cities}
              placeholder={isFetchingBranches ? 'Loading City...' : 'Select City'}
              disabled={!selection.state}
              error={locatecityError}
            />
 
            <button className="btn primary-button regular" onClick={handleLocate}>
              Locate
            </button>
          </div>
        </div>
      </div>
 
      <div className="branch-container">
        {hasLocated && branchesInSelection.length > 0 ? (
          <>
            <div className="count-of-card large-body semibold ">
              <span>
                {branchesInSelection.length}{" "}
                {branchesInSelection.length === 1 ? "branch" : "branches"}
              </span>{" "}
              found
            </div>
 
            <div className="branch-cards">
              {branchesInSelection.map((b) => {
                const id = String(b.id);
                const isActive = id === activeBranchId;
 
                return (
                  <div key={id} className="branch-card">
                    <div className="branch-card-top">
                      <div className="branch-card__address regular-body regular">
                        {stripHTMLTags(
                          b.branchAddressRawText || b.branchAddress
                        )}
                      </div>
 
                      <div className="branch-card__opening small-body regular">
                        {stripHTMLTags(b.branchOpeningTime)}
                      </div>
                    </div>
 
                    <div className="branch-card__actions">
                      <button className="btn secondary-button regular" onClick={() => openContactForBranch(id)}>
                        Get Details
                      </button>
                    </div>
 
                    {isActive && (
                      <div className="popup-modal">
                        <div className="body-popup">
                          <h6 className="large-body bold">Send to Phone</h6>
                          <button
                            className="close"
                            onClick={() => setActiveBranchId("")}
                          >
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                                fill="#3C3D3D"
                              />
                            </svg>
                          </button>
                          <form
                            className="branch-card__contact"
                            onSubmit={handleSend}
                          >
                            <div className="flag-input">
                              <div className="flag-box">
                                <img
                                  className="country-flag"
                                  src={IndiaFlag}
                                  alt="India"
                                />
                                <span className="dial-code">+91</span>
                              </div>
                              <FormInput
                                name="mobileNumber"
                                label="Mobile Number"
                                value={contactForm.mobileNumber}
                                onChange={handleContactChange}
                                error={contactErrors.mobileNumber}
                                placeholder="e.g. 9876272383"
                                maxLength={10}
                                type="tel"
                              />
                            </div>
                            <span>OR</span>
 
                            <FormInput
                              name="email"
                              label="Email"
                              value={contactForm.email}
                              onChange={handleContactChange}
                              error={contactErrors.email}
                              type="email"
                              placeholder="Deepak@indusindnipponlife.com"
                            />
 
                            <button className="btn primary-button regular" type="submit" disabled={loading}>
                              {loading ? "Sending…" : "Send"}
                            </button>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="nothing_selected-conatiner">
            <div className="nothing_selected-wraper">
              <div className="nothing_selected-image">
                <svg
                  width="238"
                  height="160"
                  viewBox="0 0 238 160"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M226.784 113.914C218.491 128.65 205.59 138.964 190.292 146.043C190.219 146.072 190.147 146.116 190.075 146.144C169.633 155.535 144.944 159.175 121.135 159.869C98.1639 160.548 76.0314 158.469 59.3299 156.143C41.9933 153.73 30.5085 151.058 30.0745 150.927C28.8033 150.537 27.5749 150.06 27.5749 150.06C26.6652 149.728 25.7543 149.338 24.8732 148.933C24.8446 148.933 24.8292 148.919 24.8006 148.904L24.5402 148.775C17.5337 145.525 11.6974 140.526 7.44978 134.429C2.32099 127.032 -0.423564 118.046 0.0532835 108.669C0.558671 98.8745 2.81211 88.0532 8.44747 77.5507C20.2367 55.5479 47.0781 34.4846 74.3548 32.5772C84.7277 31.8542 99.2614 33.1111 113.969 23.9665C117.347 21.8665 118.466 21.1221 121.609 19.1303C126.106 16.2799 149.159 1.66767 176.664 4.74519C183.411 5.50029 205.915 9.05346 222.004 27.8967C243.973 53.6286 239.452 91.4031 226.784 113.914Z"
                    fill="#FCE4E5"
                  />
                  <path
                    d="M196.75 133.057C194.366 138.359 192.084 142.793 190.292 146.045C190.219 146.073 190.147 146.117 190.075 146.146C169.633 155.536 144.944 159.176 121.135 159.871C113.318 159.929 106.095 159.582 99.5944 159.004C89.0193 152.46 84.2663 142.477 86.6505 135.542C87.2867 133.693 88.4425 132.046 90.1466 130.76C105.952 118.741 131.392 118.423 136.695 104.684C140.219 95.5249 149.076 69.982 154.579 53.9892C156.285 49.0482 168.796 35.945 183.719 36.6383C196.129 37.202 204.508 47.5606 205.332 48.6C223.924 72.2783 203.684 117.541 196.75 133.057Z"
                    fill="#3D71F2"
                  />
                  <path
                    d="M173.528 140.313C173.497 140.313 173.464 140.311 173.431 140.307L150.976 137.339C150.565 137.285 150.278 136.909 150.33 136.5C150.385 136.093 150.736 135.814 151.17 135.854L173.625 138.822C174.036 138.877 174.323 139.253 174.269 139.662C174.22 140.039 173.898 140.313 173.528 140.313Z"
                    fill="#002D53"
                  />
                  <path
                    d="M121.139 33.6903C121.139 33.6903 110.583 31.6308 112.901 20.9451C115.219 10.2606 122.17 10.389 122.17 10.389C122.17 10.389 133.628 -7.2472 154.998 3.43729C176.368 14.1218 160.662 29.3131 172.634 39.4827C184.606 49.6523 202.302 96.4631 163.845 92.0466L121.139 33.6903Z"
                    fill="#002D53"
                  />
                  <path
                    d="M150.307 140.547C149.238 142.339 145.425 148.609 138.908 152.553C119.636 164.24 90.3089 147.424 80.716 141.92C71.9318 136.878 65.3879 131.518 61.1546 127.661L30.0798 150.92C28.3032 150.371 26.5694 149.707 24.8785 148.926C24.8499 148.926 24.8356 148.912 24.8059 148.898L24.5455 148.768C17.539 145.517 11.7027 140.519 7.45508 134.422C16.6139 124.165 39.3254 100.139 52.01 99.4743C68.1468 98.6216 109.812 120.711 109.812 120.711L155.58 119.165C155.739 122.893 155.609 131.62 150.307 140.547Z"
                    fill="#004070"
                  />
                  <path
                    d="M144.053 58.9225C144.053 58.9225 125.687 70.079 126.545 74.3707C127.404 78.6623 127.404 78.6623 127.404 78.6623L109.811 120.715C115.645 122.699 122.803 124.637 131.095 125.864C140.324 127.231 148.503 127.352 155.141 127.008C157.759 118.664 174.042 65.7137 159.932 57.8927C158.775 57.2517 157.351 56.8784 155.576 56.9271L144.053 58.9225Z"
                    fill="#AE272C"
                  />
                  <path
                    d="M91.2991 94.9566L77.5217 89.326C76.6191 88.9574 76.002 87.8336 76.002 86.56V51.2305C76.002 49.2922 77.3885 47.9033 78.7632 48.4646L92.5406 54.0952C93.4432 54.4638 94.0603 55.5876 94.0603 56.8611V92.1907C94.0603 94.129 92.6738 95.5179 91.2991 94.9566Z"
                    fill="white"
                  />
                  <path
                    d="M157.574 84.4782C150.234 93.8974 142.78 100.327 136.698 104.675C134.922 105.946 133.26 107.029 131.757 107.969C121.817 114.166 117.859 114.021 116.314 113.863C102.243 112.39 95.6977 88.9851 95.3517 87.7139L85.8599 87.0777C85.8599 87.0777 79.8642 79.9845 81.0783 77.6299C82.3067 75.2754 85.3117 74.3217 85.3117 74.3217C85.3117 74.3217 78.6953 71.5332 82.0035 69.3523C85.3117 67.1559 94.3694 71.1586 94.3694 71.1586C94.3694 71.1586 93.4157 65.6112 98.4874 68.1964C103.544 70.7685 106.029 85.6198 106.029 85.6198C110.594 95.0533 118.496 94.259 126.371 89.8532C138.348 83.1785 150.237 68.1976 150.237 68.1976L157.574 84.4782Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M160.357 86.4495C157.713 89.2939 154.501 92.3822 150.66 95.4609C145.677 99.4552 140.868 102.446 136.699 104.677C133.795 100.544 129.461 94.3621 126.369 89.854C133.493 85.1878 138.011 80.3528 140.876 76.6652C144.355 72.1857 146.313 68.2175 151.006 66.8667C153.797 66.0628 157.612 66.1698 160.186 68.2544C164.194 71.5008 164.875 79.2897 160.357 86.4495Z"
                    fill="#AE272C"
                  />
                  <path
                    d="M117.404 59.5176C117.404 59.5176 116.236 53.4292 109.417 58.3642C102.599 63.2991 99.7233 65.4123 100.968 67.5099C102.212 69.6076 109.808 65.0591 109.808 65.0591"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M109.801 74.391V74.4112C108.384 74.8904 107.406 74.5111 106.747 73.7334C105.051 71.757 105.211 67.3263 105.211 67.3263C106.057 66.9469 106.901 66.5676 107.746 66.1895C108.43 68.9233 109.116 71.6572 109.801 74.391Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M111.416 74.8487C110.778 75.0283 110.239 74.8487 109.8 74.41V74.3897C109.501 74.1103 109.241 73.7119 109.042 73.2517C109.021 73.2315 109.021 73.2113 109.021 73.2113C107.864 70.657 107.744 66.1858 107.744 66.1858C108.889 64.6744 110.033 63.1618 111.178 61.6504C112.149 63.5411 113.12 65.4319 114.091 67.3226C114.091 67.325 114.829 73.8308 111.416 74.8487Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M120.039 96.245L112.056 94.7086L115.31 92.9725C107.666 92.6336 106.748 73.7333 106.748 73.7333C107.407 74.511 108.384 74.8903 109.802 74.4111C110.241 74.8499 110.779 75.0294 111.418 74.8499C114.831 73.832 114.092 67.3262 114.092 67.3262L109.462 66.4878C109.521 66.0086 109.701 65.4901 109.98 64.9312L117.345 59.5622C118.043 59.4825 118.701 59.5218 119.28 59.6621H119.36C121.955 60.2614 123.691 62.7361 123.671 65.3902V65.8088C123.591 76.7656 123.271 81.7553 125.647 82.8529C126.286 83.1323 126.585 82.9123 126.884 83.2524C128.92 85.488 120.039 96.245 120.039 96.245Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M118.981 75.268C118.907 75.268 118.829 75.2597 118.753 75.2406C117.908 75.0421 115.824 74.3583 114.693 72.2951C113.445 69.9953 113.933 67.051 115.899 64.9628C116.277 64.5609 116.908 64.5419 117.31 64.92C117.712 65.2982 117.731 65.932 117.353 66.3315C115.999 67.768 115.624 69.8276 116.445 71.3402C117.187 72.6947 118.625 73.1608 119.208 73.2988C119.746 73.426 120.077 73.9611 119.951 74.4974C119.844 74.9576 119.435 75.268 118.981 75.268Z"
                    fill="#FF7A72"
                  />
                  <path
                    d="M110.991 69.3652C110.533 69.3652 110.118 69.2832 109.741 69.1203C109.269 68.9157 108.197 68.2331 108.472 66.3448C108.552 65.7966 109.067 65.4303 109.604 65.5005C110.15 65.5802 110.526 66.0867 110.448 66.6326C110.408 66.9108 110.418 67.2367 110.536 67.2878C110.798 67.4008 111.625 67.547 113.608 66.453C114.922 65.7336 115.99 64.8786 115.999 64.8702C116.42 64.5325 117.055 64.5718 117.405 64.9797C117.753 65.3911 117.737 65.9857 117.337 66.3484L116.667 65.6099L117.329 66.3567C117.202 66.4661 116.035 67.4008 114.571 68.2022C113.165 68.9799 111.981 69.3652 110.991 69.3652Z"
                    fill="#FF7A72"
                  />
                  <path
                    d="M109.979 65.9286C109.833 65.9286 109.686 65.8977 109.546 65.8311C109.049 65.5909 108.841 64.9952 109.079 64.4981C110.069 62.4397 112.456 59.7118 115.933 58.7973C116.39 58.6808 116.812 58.6142 117.236 58.569C117.774 58.5178 118.277 58.9055 118.335 59.4537C118.393 60.0019 117.999 60.4942 117.45 60.5525C117.115 60.5893 116.782 60.6405 116.431 60.7273C113.802 61.4194 111.746 63.5539 110.879 65.3626C110.707 65.7193 110.35 65.9286 109.979 65.9286Z"
                    fill="#FF7A72"
                  />
                  <path
                    d="M126.369 90.8505C126.028 90.8505 125.696 90.6769 125.509 90.363C125.229 89.8897 125.382 89.2773 125.857 88.9943C125.968 88.9277 137.057 82.2471 143.059 72.0501C143.337 71.5721 143.952 71.4187 144.425 71.6969C144.898 71.9752 145.059 72.5876 144.778 73.0633C138.514 83.7109 127.35 90.4284 126.879 90.709C126.717 90.8065 126.543 90.8505 126.369 90.8505Z"
                    fill="#E35055"
                  />
                  <path
                    d="M155.584 56.928C153.748 58.764 141.235 71.2382 136.205 71.1573C135.925 71.1573 135.367 71.1169 135.107 70.7578C133.59 68.6827 144.049 58.9222 144.049 58.9222C143.869 58.0636 143.669 57.2265 143.49 56.3679L142.731 52.9348C142.072 53.5936 141.074 54.5116 139.737 55.4689C138.041 56.7068 134.129 59.3408 130.916 59.4406C124.908 59.6 120.877 47.5064 120.877 47.5064C119.918 47.1877 118.961 46.888 117.982 46.5681C118.701 44.4527 119.4 42.3372 120.098 40.2217C117.703 34.1142 120.098 21.841 120.098 21.841L121.136 17.9287C122.334 19.8646 126.344 25.8318 134.347 28.8855C141.592 31.6396 147.919 30.3827 150.234 29.8238C151.013 33.8157 151.811 37.7875 152.589 41.7782V41.7985C153.148 44.6322 153.707 47.4862 154.286 50.3199C154.706 52.5174 155.144 54.7328 155.584 56.928Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M155.162 37.4469C153.877 41.3509 151.006 43.9135 148.747 43.1703C146.489 42.4271 145.698 38.6599 146.983 34.7559C148.267 30.8519 151.139 28.2893 153.397 29.0326C155.656 29.7758 156.446 33.543 155.162 37.4469Z"
                    fill="#FFB3AE"
                  />
                  <path
                    d="M154.287 50.3224L143.489 56.3693L142.73 52.9362L152.59 41.7998C153.149 44.6347 153.707 47.4887 154.287 50.3224Z"
                    fill="#FF7A72"
                  />
                  <path
                    d="M191.866 142.563L190.074 146.146C169.631 155.536 144.942 159.176 121.133 159.871C98.1621 160.55 76.0297 158.47 59.3281 156.144C60.5851 151.304 65.7138 139.775 86.6476 135.543C89.3921 134.994 92.3971 134.561 95.7053 134.286C131.376 131.351 191.866 142.563 191.866 142.563Z"
                    fill="#3D71F2"
                  />
                </svg>
              </div>
              <div className="nothing_selected-text">
                <h6>Nothing selected yet</h6>
                <p>
                  Choose a State and City from the top to view content here.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
 
      {/* SUCCESS MODAL */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="You’re all set!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTEDBL}</p>
      </Modal>
      {/* ERROR MODAL */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>
    </div>
  );
};
 
export default BranchLocatorForm;